/**
 * useProductTour - Hook for managing the product tour flow
 * PRD: PRD-product-tour-organisateur.md
 *
 * Note: Tour is now opt-in only (launched via Help menu).
 * Tour completion is NOT persisted to DB - users can replay anytime.
 *
 * GA4 Analytics Events:
 * - product_tour_started: Tour launched (from help menu or replay)
 * - product_tour_completed: User finished all steps
 * - product_tour_skipped: User skipped (includes step_index where they left)
 */

import { useState, useCallback } from 'react';
import { AnalyticsBatchService } from '../services/AnalyticsBatchService';

interface UseProductTourReturn {
  /** Whether the tour is currently running */
  isRunning: boolean;
  /** Current step index (0-based) */
  currentStep: number;
  /** Total number of steps */
  totalSteps: number;
  /** Start the tour */
  startTour: () => void;
  /** End the tour without completing */
  endTour: () => void;
  /** Skip the tour (tracks which step user was on) */
  skipTour: (stepIndex?: number) => Promise<void>;
  /** Go to next step */
  nextStep: () => void;
  /** Go to previous step */
  prevStep: () => void;
  /** Complete the tour (just stops it, no DB persistence) */
  completeTour: () => Promise<void>;
  /** Replay the tour (for "Replay Tour" button) */
  replayTour: () => void;
}

const TOTAL_STEPS = 7;

/**
 * Hook to manage the product tour for organizers
 * Tour is opt-in only - launched via Help menu
 * No persistence to database (users can replay anytime)
 */
export function useProductTour(): UseProductTourReturn {
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Start the tour
  const startTour = useCallback(() => {
    setCurrentStep(0);
    setIsRunning(true);

    // GA4 Analytics
    AnalyticsBatchService.getInstance().addEvent('product_tour_started', {
      source: 'help_menu',
      total_steps: TOTAL_STEPS,
    });
  }, []);

  // End the tour without completing (user cancelled mid-tour)
  const endTour = useCallback(() => {
    setIsRunning(false);
  }, []);

  // Skip the tour (tracks which step user was on)
  const skipTour = useCallback(async (stepIndex?: number) => {
    setIsRunning(false);

    // GA4 Analytics - track where user skipped
    AnalyticsBatchService.getInstance().addEvent('product_tour_skipped', {
      step_index: stepIndex ?? currentStep,
      step_number: (stepIndex ?? currentStep) + 1, // Human-readable (1-based)
      total_steps: TOTAL_STEPS,
    });

    console.log(`⏭️ Tour skipped at step ${(stepIndex ?? currentStep) + 1}/${TOTAL_STEPS}`);
  }, [currentStep]);

  // Go to next step
  const nextStep = useCallback(() => {
    setCurrentStep((prev) => Math.min(prev + 1, TOTAL_STEPS - 1));
  }, []);

  // Go to previous step
  const prevStep = useCallback(() => {
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  }, []);

  // Complete the tour (just stops it locally)
  const completeTour = useCallback(async () => {
    setIsRunning(false);

    // GA4 Analytics
    AnalyticsBatchService.getInstance().addEvent('product_tour_completed', {
      total_steps: TOTAL_STEPS,
    });

    console.log('✅ Tour completed');
  }, []);

  // Replay the tour (for settings/help button)
  const replayTour = useCallback(() => {
    setCurrentStep(0);
    setIsRunning(true);

    // GA4 Analytics
    AnalyticsBatchService.getInstance().addEvent('product_tour_started', {
      source: 'replay',
      total_steps: TOTAL_STEPS,
    });
  }, []);

  return {
    isRunning,
    currentStep,
    totalSteps: TOTAL_STEPS,
    startTour,
    endTour,
    skipTour,
    nextStep,
    prevStep,
    completeTour,
    replayTour,
  };
}

export default useProductTour;
